/* eslint-disable jsx-a11y/label-has-associated-control */
import { graphql } from 'gatsby';
import get from 'lodash.get';
import React, { useLayoutEffect, useState } from 'react';

import ContentfulImage from '../components/Common/ContentfulImage';
import { ArrowLink } from '../components/Common/Links';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import SmartLink from '../components/Common/SmartLink';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import AdditionalResources from '../components/PageSections/AdditionalResources';
import CallToAction from '../components/PageSections/CallToAction';
import PageSection from '../components/PageSections/PageSection';
import * as styles from './caseStudy.module.scss';

const getStat = (stat) => {
  if (!stat || stat.indexOf('%') === -1) {
    return <span>{stat}</span>;
  }
  let split = stat.split('%');
  return (
    <>
      <span>{split[0]}</span>
      <span className={styles.percent}>%</span>
    </>
  );
};

const Attachment = ({ page }) => {
  if (!page.attachmentLabel) return null;
  return (
    <div className={styles.attachment}>
      <SmartLink to={page?.attachment?.file?.url} isExternal={true} download={true}>
        <img src={page?.attachmentImage?.file?.url} alt={page?.attachmentImage?.title} />
      </SmartLink>
      <ArrowLink
        className={styles.ctaLink}
        label={page.attachmentLabel}
        url={get(page, 'attachment.file.url')}
        isExternal={true}
        isDownload
        inlineArrow
        hoverAnimation
      />
    </div>
  );
};

const SideBar = ({ page }) => {
  const firstStat = getStat(page.firstStat);
  const secondStat = getStat(page.secondStat);
  const thirdStat = getStat(page.thirdStat);

  return (
    <sidebar className={styles.sidebarWrapper}>
      <div className={styles.sidebar}>
        {page.customerLogo && (
          <ContentfulImage imageFragment={page.customerLogo} lazy={false} className={styles.logo} />
        )}
        {page.industryTypes && (
          <div className={styles.labelGroup}>
            <p className={styles.label}>Industry</p>
            <p>{page.industryTypes[0].title}</p>
          </div>
        )}
        {page.companySize && (
          <div className={styles.labelGroup}>
            <p className={styles.label}>Company Size</p>
            <p>{page.companySize}</p>
          </div>
        )}
        {page.partners && (
          <div className={styles.labelGroup}>
            <p className={styles.label}>Partners</p>
            <div className={styles.partnerLogos}>
              {page.partners.map((partner, i) => (
                <ContentfulImage imageFragment={partner} key={i} lazy={true} />
              ))}
            </div>
          </div>
        )}
        {page.firstStat && (
          <div className={styles.labelGroup}>
            <p className={styles.label}>Results</p>
          </div>
        )}
        {page.firstStat && (
          <p>
            <span className={styles.stat}>{firstStat} </span>
            {page.firstStatLabel}
          </p>
        )}
        {page.secondStat && (
          <p>
            <span className={styles.stat}>{secondStat} </span>
            {page.secondStatLabel}
          </p>
        )}
        {page.thirdStat && (
          <p>
            <span className={styles.stat}>{thirdStat} </span>
            {page.thirdStatLabel}
          </p>
        )}
      </div>
      <Attachment page={page} />
    </sidebar>
  );
};

const CaseStudy = ({ data: { page, relatedCaseStudies } }) => {
  const [selectedCaseStudies, setSelectedCaseStudies] = useState({ content: [] });

  useLayoutEffect(() => {
    // Randomly select 4 related case studies
    const shuffledCaseStudies = relatedCaseStudies.nodes.sort(() => 0.5 - Math.random());
    setSelectedCaseStudies({
      content: shuffledCaseStudies.slice(0, 3).map((caseStudy) => ({
        __typename: 'ContentfulPageCaseStudy',
        slug: caseStudy.slug,
        heading: caseStudy.heading,
        image: caseStudy.pageMetaData?.ogImage,
      })),
    });
  }, [relatedCaseStudies]);

  return (
    <Layout>
      <PageSection isTopSection>
        <div className={styles.copyContainer}>
          <div className={styles.mainCopy}>
            <h1 className={styles.heading}>{page.heading}</h1>
            <RichTextConverter copy={page.mainCopy} />
          </div>
          <SideBar page={page} />
        </div>
      </PageSection>
      {selectedCaseStudies?.content?.length > 0 && (
        <PageSection slim className={styles.relatedCaseStudies}>
          <AdditionalResources section={selectedCaseStudies} showImage={true} cardColor={'alternate'} />
        </PageSection>
      )}
      {page.footerForm && (
        <PageSection className={styles.cta}>
          <CallToAction
            copy={get(page, 'footerForm.formCopy')}
            form={page.footerForm}
            formId={get(page, 'footerForm.marketoFormId')}
          />
        </PageSection>
      )}
    </Layout>
  );
};

export default CaseStudy;

export const Head = ({ data: { page }, location }) => (
  <>
    <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
    <meta name="zd-site-verification" content={process.env.GATSBY_ZENDESK_CRAWLER_KEY} />
  </>
);

export const query = graphql`
  query CaseStudyQuery($slug: String!, $industry: String!) {
    page: contentfulPageCaseStudy(slug: { eq: $slug }) {
      ...CaseStudy
    }
    relatedCaseStudies: allContentfulPageCaseStudy(
      filter: {
        node_locale: { eq: "en-US" }
        industryTypes: { elemMatch: { title: { eq: $industry } } }
        slug: { ne: $slug }
      }
    ) {
      nodes {
        heading
        customerLogo {
          ... on Node {
            ...ContentfulImage
          }
        }
        slug
        pageMetaData {
          ogImage {
            ... on Node {
              ...ContentfulImage
            }
          }
        }
      }
    }
  }
`;
